import {
  CLICK_SOFT_CAP_BIGINT,
  COMBO_MAX_STACKS,
  COMBO_PCT_PER_STACK,
  CLICK_COOKIE_MULT,
  OFFLINE_THRESHOLD_MS,
  OFFLINE_MAX_MS,
  OFFLINE_BASE_RATE,
  OFFLINE_EFFICIENCY_BPS,
  OFFLINE_SORTER_BPS_PER_LEVEL,
  OFFLINE_SORTER_MAX_LEVELS,
  OFFLINE_MAX_EFFICIENCY_BPS,
  PASSIVE_DIVISOR,
  PASSIVE_MAX_SORTER_LEVEL,
  PASSIVE_BOOST_PCT_PER_LEVEL,
  SETTLE_CAP_SECS,
  SETTLE_CAP_MS,
} from "./config"

type ModuleInput = { baseCps: bigint; level: number };
type BakerySettleState = { cookies: bigint; cps: bigint; lastSettledAt: number };

const CLICK_SOFT_CAP = CLICK_SOFT_CAP_BIGINT;

export function effectiveClickPower(power: bigint): bigint {
  if (power <= CLICK_SOFT_CAP) return power < 0n ? 0n : power;
  return CLICK_SOFT_CAP + ((power - CLICK_SOFT_CAP) * 2n) / 3n;
}

export function effectiveClickPowerNum(power: number): number {
  if (!Number.isFinite(power) || power <= 0) return 1;
  const p = BigInt(Math.floor(power));
  const eff = effectiveClickPower(p);
  return Number(eff);
}

export function escalateCost(base: bigint, steps: number): bigint {
  const lv = Math.max(0, Math.floor(steps));
  if (lv === 0) return base;
  if (base <= 0n) return base;
  const cost = (base * 3n ** BigInt(lv)) / (2n ** BigInt(lv));
  return cost < base ? base : cost;
}

export function escalateCostStr(baseStr: string, steps: number): string {
  const base = BigInt(baseStr);
  return escalateCost(base, steps).toString();
}

function comboMultiplierBps(combo: number): number {
  if (!Number.isFinite(combo) || combo <= 0) return 10000;
  return 10000 + Math.min(Math.floor(combo), COMBO_MAX_STACKS) * COMBO_PCT_PER_STACK * 100;
}

export function calculateClickReward(clickPower: bigint, eventMult: number, combo?: number, isCrit?: boolean): bigint {
  const scaled = BigInt(Math.max(0, Math.round(eventMult * 100)));
  if (scaled <= 0n) return 0n;
  const base = effectiveClickPower(clickPower);
  if (base <= 0n) return 0n;
  const comboVal = typeof combo === "number" ? combo : 0;
  const crit = typeof isCrit === "boolean" ? isCrit : false;
  let reward = (base * scaled) / 100n;
  const comboBps = comboMultiplierBps(comboVal);
  if (comboBps !== 10000) reward = (reward * BigInt(comboBps)) / 10000n;
  if (crit) reward *= 2n;
  reward *= CLICK_COOKIE_MULT;
  return reward;
}

export function calculateBakerCredits(amount: bigint): bigint {
  if (amount <= 0n) return 0n;
  const v = (amount + 1n) / 2n;
  return v > 0n ? v : 1n;
}

export function calculateProduction(modules: ModuleInput[], automation: ModuleInput[]): bigint {
  let total = 0n;
  for (const m of modules) total += m.baseCps * BigInt(Math.max(0, m.level));
  for (const m2 of automation) total += m2.baseCps * BigInt(Math.max(0, m2.level));
  return total < 0n ? 0n : total;
}

export function calculateUpgradeCost(base: bigint, level: number): bigint {
  return escalateCost(base, level);
}

export function settleProduction(bakery: BakerySettleState, nowMs: number): BakerySettleState {
  if (!Number.isFinite(nowMs)) return bakery;
  if (nowMs <= bakery.lastSettledAt) return bakery;
  const elapsedMs = nowMs - bakery.lastSettledAt;
  if (elapsedMs < 1000) return bakery;
  const elapsedSec = Math.floor(elapsedMs / 1000);
  const secs = Math.min(elapsedSec, SETTLE_CAP_SECS);
  if (secs <= 0) return bakery;
  const gained = bakery.cps * BigInt(secs);
  bakery.cookies += gained;
  bakery.lastSettledAt += secs * 1000;
  return bakery;
}

export type EconomyOfflineBonus = { cookies: bigint; credits: bigint; hours: number; rate: number; offlineMs: number };
export function calculateOfflineBonus(nowMs: number, lastSeenAt: number, autoCps: bigint | number, sorterOfflineLevels = 0): EconomyOfflineBonus | null {
  if (!Number.isFinite(nowMs) || !Number.isFinite(lastSeenAt)) return null;
  if (lastSeenAt <= 0) return null;
  const elapsed = nowMs - lastSeenAt;
  if (!Number.isFinite(elapsed) || elapsed <= OFFLINE_THRESHOLD_MS) return null;
  const offlineMs = Math.min(elapsed, OFFLINE_MAX_MS);
  let cps = 0n;
  try {
    cps = typeof autoCps === "bigint" ? autoCps : BigInt(Math.max(0, Math.floor(autoCps)));
  } catch { cps = 0n }
  if (cps < 0n) cps = 0n;
  const sorterLv = Number.isFinite(sorterOfflineLevels) ? Math.max(0, Math.min(OFFLINE_SORTER_MAX_LEVELS, Math.floor(sorterOfflineLevels))) : 0;
  const effBps = Math.min(OFFLINE_MAX_EFFICIENCY_BPS, OFFLINE_EFFICIENCY_BPS + OFFLINE_SORTER_BPS_PER_LEVEL * sorterLv);
  const offlineSecs = Math.floor(offlineMs / 1000);
  const autoCookies = offlineSecs > 0 && cps > 0n ? (cps * BigInt(offlineSecs) * BigInt(effBps)) / 10000n : 0n;
  const baseCookies = (BigInt(OFFLINE_BASE_RATE) * BigInt(offlineMs)) / 3600000n;
  const cookies = autoCookies + baseCookies;
  if (cookies <= 0n) return null;
  const credits = (cookies + 1n) / 2n;
  const hours = offlineMs / 3600000;
  const rate = offlineMs > 0 ? Number((cookies * 3600000n) / BigInt(offlineMs)) : 0;
  return { cookies, credits, hours, rate, offlineMs };
}

export function calculatePassiveCredits(cps: bigint, elapsedMs: number, remainder: bigint, sorterLevel = 0): { credits: bigint; remainder: bigint } {
  if (cps <= 0n || !Number.isFinite(elapsedMs) || elapsedMs <= 0 || remainder < 0n) return { credits: 0n, remainder };
  const ms = Math.floor(elapsedMs);
  const clamped = Math.min(ms, SETTLE_CAP_MS);
  if (clamped <= 0) return { credits: 0n, remainder };
  const total = cps * BigInt(clamped) + remainder;
  const lvl = Math.max(0, Math.floor(Number.isFinite(sorterLevel) ? sorterLevel : 0));
  const capped = Math.min(lvl, PASSIVE_MAX_SORTER_LEVEL);
  const boostedTotal = capped <= 0 ? total : (total * BigInt(100 + PASSIVE_BOOST_PCT_PER_LEVEL * capped)) / 100n;
  const credits = boostedTotal / PASSIVE_DIVISOR;
  const nextRemainder = boostedTotal % PASSIVE_DIVISOR;
  return { credits, remainder: nextRemainder };
}
